import React, { useState, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import Icon from '../../../components/AppIcon';
import { getCachedSignedUrl } from '../../../utils/signedUrlCache';

const NativePdfViewer = ({ filePath, fileUrl, fileName, t, preloadedUrl }) => {
  const [loadState, setLoadState] = useState('loading');
  const [signedUrl, setSignedUrl] = useState(preloadedUrl || null);
  const [numPages, setNumPages] = useState(null);

  const lowerName = (fileName || filePath || '')?.toLowerCase();
  const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i?.test(lowerName);

  useEffect(() => {
    setLoadState('loading');
    setNumPages(null);

    if (!filePath) return;

    if (preloadedUrl) {
      setSignedUrl(preloadedUrl);
      return;
    }

    setSignedUrl(null);

    const fetchSignedUrl = async () => {
      try {
        const url = await getCachedSignedUrl(filePath, 3600);
        setSignedUrl(url);
      } catch (err) {
        console.error('Failed to get signed URL:', err);
        setLoadState('error');
      }
    };

    fetchSignedUrl();
  }, [filePath, preloadedUrl]);

  if (!filePath) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-amber-200 h-full overflow-hidden flex flex-col items-center justify-center gap-3 p-6">
        <div className="w-14 h-14 rounded-xl bg-amber-50 flex items-center justify-center">
          <Icon name="AlertTriangle" size={28} className="text-amber-500" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-gray-700">⚠️ File needs to be re-uploaded</p>
          <p className="text-xs text-gray-400 mt-1">This is a legacy file. Please delete and re-upload to enable secure viewing.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full overflow-hidden relative flex flex-col">
      {(loadState === 'loading' || !signedUrl) && loadState !== 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#F8F9FA] z-10">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-gray-500">{t('invoiceInbox.loadingDocument')}</p>
        </div>
      )}

      {loadState === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#F8F9FA] z-10">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
            <Icon name="FileText" size={24} className="text-gray-400" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-gray-700">{t('invoiceInbox.unableToLoad')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('invoiceInbox.unableToLoadSub')}</p>
          </div>
          <div className="flex items-center gap-2">
            {signedUrl && (
              <a href={signedUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors">
                <Icon name="ExternalLink" size={14} />
                {t('invoiceInbox.openInNewTab') || 'Open in new tab'}
              </a>
            )}
            {signedUrl && (
              <a href={signedUrl} target="_blank" rel="noopener noreferrer" download={fileName}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
                <Icon name="Download" size={14} />
                {t('invoiceInbox.downloadFile')}
              </a>
            )}
          </div>
        </div>
      )}

      {signedUrl && loadState !== 'error' && (
        <div className="flex-1 overflow-y-auto flex flex-col items-center py-4 px-2 gap-3">
          {isImage ? (
            <img
              src={signedUrl}
              alt={fileName || 'Invoice'}
              className="max-w-full shadow-md rounded"
              onLoad={() => setLoadState('loaded')}
              onError={() => { console.error('Image load error for:', fileName); setLoadState('error'); }}
            />
          ) : (
            <Document
              file={signedUrl}
              onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoadState('loaded'); }}
              onLoadError={(err) => { console.error('PDF load error:', err); setLoadState('error'); }}
              loading={null}
            >
              {numPages
                ? Array.from(new Array(numPages), (_, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={Math.min(window.innerWidth * 0.55, 700)}
                      className="shadow-md rounded mb-2"
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  ))
                : null}
            </Document>
          )}
        </div>
      )}
    </div>
  );
};

export default NativePdfViewer;
